import { PermissionRoles } from "@/enum";
import { CourseProgressEnum } from "@/enum/course-progress.enum";
import { PARTICIPANT_PERMISSION_NONE } from "@/pages/Private/pages/Course/components/SelectParticipantPermission";
import { CourseStatus } from "@/pages/Private/pages/Courses/enums/CourseStatus.enum";

export default {
	translation: {
		companies: {
			title: "Company Management",
			subtitle: "Manage your teams and user permissions.",
			search: "Search for company",
			name: "Name",
			activated: "AI Features activated",
			"not-activated": "AI Features not activated",
			linkedinUrl: "LinkedIn URL",
			users: "Users",
			create: {
				button: "Add New Company",
				title: "Add New Company",
			},
			delete: {
				title: "Delete Company",
			},
			update: {
				title: "Update Company",
			},
		},
		defaultParticipants: {
			title: "Default Participants",
			yesterday: "Yesterday",
			selectParticipants: "Select default participants for this scenario",
			description: "Reusable participant templates for your company's scenarios.",
			addParticipant: "Add participant",
			editParticipant: "Edit participant",
			internalRole: "Internal role",
			lastUsed: "Last used on",
			notUsed: "Not used",
			deleteRecentlyUsedTitle: "Are you sure?",
			deleteRecentlyUsedWarning: "This participant has been recently used.",
			scenarioRoles: "Scenario roles",
			scenarioRolesHint: "Separate roles with commas (e.g., role1, role2).",
			companyOwnership: "Company ownership",
			noParticipants: "No default participants yet.",
			loadError: "The default participants could not be loaded. Please try again.",
		},
		company: {
			general: "General Settings",
			title: "Company Management",
			subtitle: "Manage your team members and their account permissions here.",
			tabs: {
				general: "General",
				user: "Users",
				roles: "User roles",
			},
			kpiCollection: {
				title: "KPI Data Collection",
				description:
					"When enabled, key performance indicators (KPIs) from your organization will be collected to help analyze trends and improve services. All data is anonymized, and no personally identifiable information is stored. You can disable KPI collection at any time, but anonymous usage statistics will still be collected for general analysis.",
				label: "Enable KPI data collection",
			},
			companyInformation: "Company Information",
			autoInvite: "Auto Invite to your company linkedin page",
			apolloApiKey: "Apollo API Key",
			lemlistApiKey: "Lemlist API Key",
			"invite-company-members": "Invite company members",
			"invite-company-members-description": "Invite your colleagues to collaborate.",
			"add-another": "Add another",
			"send-invite": "Send invite",

			"company-members": "Company members",
			"company-member-description": "Manage your existing team and change roles/permissions.",
		},
		videoWizard: {
			title: "Video assistant",
			subtitle:
				"A guided six-step flow — every step shows its model, the estimate up front and the actual cost afterwards.",
			backToScenario: "Back to scenario",
			/** What the video is narrated and subtitled in; the images are prompted in German regardless. */
			languages: {
				DE: "German",
				EN: "English",
			},
			status: {
				notStarted: "Not started yet",
				// Not "In progress": it read as the system being busy (BCM360-373).
				awaitingApproval: "Awaiting approval",
			},
			stepper: {
				running: "Running …",
				approved: "approved",
				// "in progress" read as a running job, locked steps included (BCM360-373).
				awaitingApproval: "awaiting approval",
				waitingFor: "waiting for step {{step}}",
				pending: "not started",
				approvedCost: "{{amount}} · approved",
				spentSoFar: "{{amount}} so far",
				estimated: "estimated ~{{amount}}",
				lockedHint: "Complete the previous step first.",
			},
			modelRow: {
				// Not "AI model": the cut step's entry is the ffmpeg render pipeline.
				label: "Model for this step",
				placeholder: "Select a model",
				estimate: "Estimate: ~{{amount}} / run",
				actual: "Actual so far: {{amount}} ({{times}}×)",
				noCostYet: "No cost incurred yet",
			},
			units: {
				PER_IMAGE: "/ image",
				PER_SECOND: "/ second",
				PER_SCENE: "/ scene",
				PER_1K_TOKENS: "/ 1,000 tokens",
				PER_TRACK: "/ narration track",
				PER_EXPORT: "/ export",
			},
			rail: {
				videoCosts: "Cost of this video",
				generationCount: "generated {{times}}×",
				actualSoFar: "Actual so far",
				forecast: "Total forecast",
				budgetHint: "{{percent}} % of the budget band ({{min}}–{{max}} per 30 s video)",
				scenarioCosts: "Total scenario cost",
				thisVideo: "Video (this assistant)",
				phaseMedia: "Phase media (images & TTS)",
				scenarioTotal: "Scenario total",
			},
			archive: {
				button: "Archive",
				title: "Archive this assistant?",
				body: "The assistant is ended and archived. It cannot be opened or continued afterwards.",
				costsRemain: "Nothing is deleted; the costs incurred stay recorded.",
				introStays: "An intro video already linked stays on the scenario.",
				newStart: "A new start for this scenario begins with an empty assistant.",
				confirm: "Archive",
				cancel: "Cancel",
				workInFlight: "You can archive once no generation is running.",
			},
			confirmReset: {
				title: "Reset the following steps?",
				body: "This step has already been approved. Generating again resets every step after it.",
				affectedSteps: "Affected steps: {{steps}}",
				costsRemain: "Costs already incurred stay on record.",
				confirm: "Continue",
				cancel: "Cancel",
			},
			errors: {
				couldNotStart: "The assistant could not be started.",
				staleVersion: "The assistant was changed elsewhere. The current state has been reloaded.",
				generic: "The action could not be completed.",
				jobFailed: "The generation failed. Please try again.",
				providerModerated:
					"The provider's safety filter refused this request. Rephrase the scene description and start the generation again.",
				sessionArchived:
					"This assistant was archived and can no longer be edited. Start a new one from the scenario.",
				providerImageRefused:
					'The video model refused this scene\'s starting image because it may show a real person. Choose another model under "Model for this step" above, or regenerate the image in step 2 without a recognisable face. Trying again unchanged will be refused the same way.',
			},
			steps: {
				storyboard: {
					title: "Storyboard",
					heading: "1 · Storyboard",
					description:
						"Enter a prompt and a target length — the language model writes a storyboard with clear instructions per scene: what is on screen and how long it runs. Iterate until you are happy with it.",
					approvedBadge: "✓ Approved",
					promptPlaceholder:
						"Describe the scenario … e.g. “A night-time chemical incident at a warehouse: the alert, the approach, the first assessment, the handover to the trainees.”",
					promptLabel: "Prompt",
					lengthLabel: "Target length",
					lengthOption: "{{seconds}} seconds",
					lengthHint: "Predefined lengths — they drive the number and duration of the scenes.",
					languageLabel: "Language",
					languageHint:
						"What the video is narrated and subtitled in. Chosen here because the scenes are written in it — changing it later rewrites them.",
					generate: "Generate storyboard",
					promptRequired:
						"Available once a scenario prompt is written — without one the model invents a scenario.",
					durationBounds: "{{min}}–{{max}} s",
					generating: "Generating the storyboard …",
					empty:
						"No storyboard yet. Set a prompt and a target length, then generate — the language model gives you a clear instruction per scene: what is on screen and how long it runs.",
					versionHeading: "Storyboard — version {{version}} · {{note}}",
					noNote: "no note",
					sceneTitle: "Scene title",
					sceneDescription: "Scene description",
					sceneDuration: "Scene duration in seconds",
					seconds: "s",
					addScene: "Add scene",
					removeScene: "remove",
					totalLength: "Total length: {{seconds}} s",
					targetMet: "Target: {{seconds}} s ✓",
					targetMissed: "Target: {{seconds}} s — adjust or generate again",
					iterateHeading: "Iterate until it fits",
					iterations_one: "{{count}} iteration",
					iterations_other: "{{count}} iterations",
					feedbackPlaceholder:
						"Describe what to change … e.g. “Show scene 2 from the responders’ perspective and bring the total to exactly 30 s”",
					regenerate: "Generate again with feedback",
					approve: "Approve storyboard → choose a style",
					approveBlockedHint: "The total length must match the target length exactly.",
				},
				style: {
					title: "Style & example images",
					heading: "2 · Graphical style & example images",
					description:
						"Only once the storyboard is approved: pick a style (photorealistic, black and white, …). Each scene then gets an example image plus its scene description. Every image can be iterated on its own.",
					approvedBadge: "✓ Approved",
					styleLabel: "Graphical style",
					styleHint:
						"The style is applied to every scene as a shared look — that is what keeps the whole video consistent.",
					styles: {
						photo: { name: "Photorealistic", hint: "As photographed, natural light" },
						bw: { name: "Black and white", hint: "High contrast, no colour" },
						illu: { name: "Illustration", hint: "Clean outlines, flat colours" },
						"3d": { name: "3D render", hint: "Technical visualisation" },
						aqua: { name: "Watercolour", hint: "Soft washes, muted" },
						doku: { name: "Documentary / archive", hint: "Desaturated, reportage feel" },
					},
					staleNotice: "🎨 Style changed — regenerate the images to apply it.",
					empty:
						"No example images yet. Pick a style and generate — each scene gets an example image plus its scene description, which is what the video generation later builds on (image-to-video).",
					generateAll: "Generate {{scenes}} example images",
					resultHeading:
						"Result: example image + scene description — iterate scene by scene until it fits",
					sceneNumber: "Scene {{number}} · {{seconds}} s",
					sceneGenerating: "generating …",
					sceneReady: "✓ generated",
					sceneStale: "Style changed — regenerate",
					sceneEmpty: "No image yet",
					sceneFeedbackLabel: "Feedback on scene {{number}}",
					sceneFeedbackPlaceholder: "Feedback on this image …",
					sceneRegeneratingWith: "Regenerating with:",
					uploadStyle: "Analyse image style",
					sourceComputer: "Upload from computer",
					sourceLibrary: "Choose from media library",
					uploadStyleHint: "or take the look from your own picture",
					uploadedStyle: "Uploaded style",
					uploadedAnalysing: "Reading the look …",
					uploadedReady: "Look read from your picture",
					uploadedFailed: "The style could not be read from this picture.",
					uploadFailed: "The picture could not be uploaded. Try again.",
					fragmentLabel: "Style description used for every scene",
					fragmentPlaceholder: "How the scenes should look …",
					fragmentEdited: "edited",
					fragmentHint:
						"This text is what the image model is given \u2014 the picture itself is not. Editing it marks this style's images for regeneration.",
					downloadImage: "Download",
					regenerateScene: "New image",
					iterateHeading: "Iterate on all scenes together",
					iterations_one: "{{count}} image generated",
					iterations_other: "{{count}} images generated",
					feedbackPlaceholder:
						"Feedback across all scenes … e.g. “darker overall, more rain, fewer people in frame”",
					regenerateAll: "Regenerate all images",
					approve: "Approve images → narrator & audio",
					approveBlockedHint:
						"Available once every scene has a current image in the selected style.",
				},
				narration: {
					title: "Narration",
					heading: "3 · Narration",
					description:
						"The narration script is written and checked: length, tone and language. Iterate with feedback until you like the words — the voice only speaks them in the next step, so iterating here never costs a voice track.",
					approvedBadge: "✓ Approved",
					generatingBadge: "Writing the script",
					governanceHeading: "Narration script & governance",
					governanceHint:
						"Every narration text is checked for language, length, tone, safety and formatting.",
					sceneNumber: "Scene {{number}} · {{title}}",
					checkPassed: "Check passed",
					checkFailed: "Check failed",
					wordCount: "{{words}} of {{target}} target words",
					repaired: "corrected {{times}}×",
					rules: {
						format: "Format",
						language: "Language",
						length: "Length",
						tone: "Tone",
						safety: "Safety",
					},
					iterateHeading: "Generate & iterate the script",
					sceneFeedbackPlaceholder:
						"Feedback for this scene only, e.g. “tighter, fewer technical terms” …",
					regenerateScene: "Rewrite this scene",
					editText: "Edit text",
					saveText: "Apply",
					cancelEdit: "Cancel",
					briefLabel: "Script brief",
					briefHint:
						"What kind of script this should be — tone, audience, emphasis. Applies to every generation until you change it.",
					briefPlaceholder:
						"e.g. “direct address, focus on self-protection, for new employees with no prior knowledge” …",
					generate: "Generate script",
					regenerate: "Regenerate",
					generating: "Writing the script …",
					generatingHint:
						"The script is being written and checked. Once it is ready you can read, iterate and approve it.",
					generateHint:
						"Writes the full narration script for every scene. Read it, iterate with feedback if needed, then approve.",
					feedbackPlaceholder:
						"Notes for the next draft, e.g. “less formal” or “tighten the first scene” …",
					approve: "Approve → Audio",
					approveBlockedChecks:
						"Available once every scene passes its checks — an overlong text would be cut off in the video.",
				},
				audio: {
					title: "Audio",
					heading: "4 · Audio",
					description:
						"Choose the speaker's voice (German is fully supported and enforced) and the subtitle style, then voice the approved script. Changing the voice or subtitles later only costs a new take — the words survive it.",
					approvedBadge: "✓ Approved",
					generatingBadge: "Generating narration",
					languageLabel: "Narration language",
					languageSupported: "Multilingual voices — every one can narrate in {{language}}",
					voiceLabel: "Speaker's voice",
					noVoice: "No voice chosen yet",
					noVoiceHint: "The narration cannot be generated without a voice.",
					voiceChosen: "This voice reads the entire narration.",
					chooseVoice: "Choose a voice",
					changeVoice: "Change voice",
					musicEmpty:
						'No tracks have been added to the music library yet. The video will be produced without background music — your platform administrator can upload or generate tracks under "Formats & presets".',
					subtitleLabel: "Subtitle style",
					subtitleNoneSample: "— no subtitles —",
					subtitleStyles: {
						NONE: "None",
						CLASSIC: "Classic (bar)",
						WORD_HIGHLIGHT: "Modern (word highlight)",
					},
					positionLabel: "Position",
					positions: {
						BOTTOM_CENTER: "Bottom centre",
						BOTTOM_LEFT: "Bottom left",
						TOP_CENTER: "Top centre",
					},
					sizeLabel: "Size",
					sizes: { S: "Small", M: "Medium", L: "Large" },
					sceneNumber: "Scene {{number}} · {{title}}",
					trackHeading: "Voiced scenes",
					trackHint: "Each scene with its spoken track — listen before you approve.",
					iterateHeading: "Voice preview & approval",
					prices: "Preview: ~{{preview}} · full track: ~{{track}}",
					preview: "Hear a preview with \u201C{{voice}}\u201D",
					previewRunning: "Generating the preview …",
					approve: "Approve → scene videos",
					generate: "Generate narration",
					regenerate: "Re-voice",
					generating: "Generating the narration …",
					generatingHint:
						"The approved script is being voiced by the chosen speaker. Once the track is ready you can listen and approve it.",
					generateHint:
						"Voices the approved script with the chosen speaker. Listen and approve afterwards.",
					approveBlockedHint: "Available once a speaker's voice has been chosen.",
					noScriptHint: "Available once the narration script (step 3) exists.",
				},
				sceneVideos: {
					title: "Scene videos",
					heading: "5 · Generate scene videos",
					noVoiceHint:
						"Note: these clips carry only the video model's ambience, not the narration. The voice track generated in step 4 is mixed in per scene at export and timed to your cut.",
					description:
						"Every scene is generated separately. Each one gives you a video plus its scene description. Iterate and approve them one by one — only then do you move on to the cut.",
					approvedBadge: "✓ Approved",
					renderingBadge_one: "● {{count}} scene rendering",
					renderingBadge_other: "● {{count}} scenes rendering",
					reviewBadge: "In review",
					empty:
						"No scene videos yet. Each scene is generated separately from its approved example image (image-to-video) — the narration track is timed per scene at export.",
					generateAll: "Generate all {{scenes}} scenes",
					asyncHint:
						"Asynchronous jobs: each scene renders on its own and appears here as soon as it is finished. You can leave this page in the meantime.",
					resultHeading: "Review and approve each scene",
					sceneNumber: "Scene {{number}} · {{seconds}} s",
					sceneGenerating: "rendering …",
					sceneReady: "Awaiting review",
					sceneApproved: "✓ approved",
					sceneStale: "Image changed — regenerate",
					sceneEmpty: "No video yet",
					sceneCost: "Cost: {{amount}} ({{times}} renders)",
					sceneFeedbackLabel: "Feedback on scene {{number}}",
					sceneFeedbackPlaceholder:
						"Movement, camera, ambience … e.g. \u201cslower camera move\u201d",
					sceneRegeneratingWith: "Regenerating with:",
					regenerateScene: "Regenerate",
					// No "✓" here — the button already carries the check icon.
					approveScene: "Approve scene",
					iterateHeading: "Continue once every scene is approved",
					progress: "{{approved}} of {{scenes}} approved",
					progressRendering: "{{approved}} of {{scenes}} approved · {{times}} rendering",
					feedbackPlaceholder:
						"Feedback for every scene — movement, camera work and ambience … e.g. \u201ccalmer camera moves overall, less motion in frame\u201d",
					feedbackHint:
						"Each scene is animated from its approved step-2 image, so feedback here steers movement, camera work and ambience — not the look. A different look means regenerating the image in step 2, which replaces the video anyway.",
					regenerateAll: "Regenerate all scenes",
					approve: "All scenes approved → Cut & export",
					approveBlockedHint:
						"The cut step unlocks once all {{scenes}} scenes have been approved individually.",
				},
				cutExport: {
					title: "Cut & export",
					heading: "6 · Cut & export",
					description:
						"Join every scene into one video. Click a clip in the timeline and trim its start and end with the handles in the cut editor; reorder by drag & drop or with ◀ ▶.",
					locked: "Unlocked once every scene video has been approved.",
					awaitingApprovalBadge: "Awaiting approval",
					renderingBadge: "● Video is being assembled",
					exportedBadge: "✓ Exported",
					timelineHeading: "Timeline",
					timelineHint:
						"Click a clip to trim it · drag (or use ◀ ▶) to reorder. Trimmed-away regions are hatched.",
					total: "Total: {{total}} (target {{target}} s)",
					unsaved: "Unsaved",
					saveNow: "Save the cut now",
					clipLabel: "Scene {{number}}",
					clipTrimmed: "{{kept}} (of {{total}})",
					clipHint: "Click to trim · drag to reorder",
					moveLeft: "Move scene {{number}} left",
					moveRight: "Move scene {{number}} right",
					trimHeading: "✂ Trimming: scene {{number}} · {{title}}",
					// It plays the cut with voice and subtitles — more than the trim alone.
					playTrim: "Preview the result",
					resetClip: "Reset",
					handleIn: "Start of the trim",
					handleOut: "End of the trim",
					trimStart: "Start",
					trimEnd: "End",
					nudgeMinus: "−0.2 s",
					nudgePlus: "+0.2 s",
					kept: "Keeping {{kept}} of {{total}}",
					voiceHeading: "Narration",
					voiceHint:
						"Drag the block to make the voice start later. Drag an edge to cut the head or tail off the take. Subtitles follow the voice automatically; speech that is cut is reported at export.",
					voiceMissing: "This scene has no narration yet — generate it in step 4.",
					voicePlay: "▷ Play",
					voiceReset: "↺ Reset narration",
					voiceOffset: "Starts at",
					voiceKept: "Speech: {{kept}} of {{total}}",
					voiceBlock: "Position the narration",
					voiceCutHead: "Cut the start of the take",
					voiceCutTail: "Cut the end of the take",
					trimHint:
						"Drag the handles or fine-tune with the buttons — trimmed-away regions are hatched (minimum length {{min}} s). The narration and the subtitles are re-timed automatically at export.",
					exportHeading: "Assemble & export",
					transitionLabel: "Transitions",
					transitions: {
						HARD_CUT: "Hard cut",
						CROSSFADE_04: "Crossfade (0.4 s)",
						FADE_BLACK: "Fade to black",
					},
					destinationLabel: "Use for",
					destinationIntro: "The scenario's intro video",
					destinationLibrary: "The media library only",
					destinationIntroHint:
						"The finished video is linked as the scenario's intro and replaces any previous one.",
					destinationLibraryHint:
						"The finished video goes to the media library. From there you can use it as an intro, as phase content, or in another scenario.",
					formatsLabel: "Output formats",
					formatsHint:
						"One render job covers every chosen format — the export is priced flat, however many formats it writes.",
					formatChip: "{{label}} · {{width}}×{{height}}",
					export: "Assemble & export",
					exportAgain: "Export again",
					exporting: "Assembling …",
					exportHint:
						"The render runs in the background: trims, narration, music and subtitles are combined. You can leave this page while it works.",
					finishedHeading: "Finished intro video",
					subtitleTrackLabel: "German",
					subtitleDownload: "Subtitle file (.vtt)",
					downloadButton: "Download",
					linkedBadge: "✓ Linked as the scenario's intro",
					libraryBadge: "✓ Saved to the media library",
					// Export warnings, phrased here from their code and structured details —
					// the server's message is German prose and only the fallback.
					warningTexts: {
						NARRATION_TRUNCATED:
							"Of the narration for “{{sceneTitle}}” ({{takeSeconds}} s), {{droppedSeconds}} s are not heard in the finished video — the cut is {{keptSeconds}} s long and the voice is trimmed or shifted inside it.",
						TRANSITION_SHORTENED:
							"Crossfades overlap the clips: the finished video is {{seconds}} s shorter than the sum of the cuts.",
						SUBTITLE_STYLE_FALLBACK:
							"No word timings exist for {{scenes}} — these subtitles use the classic style. Regenerate the narration to get word highlights.",
						MUSIC_UNAVAILABLE:
							"The chosen background music could not be loaded — the video was created without music.",
					},
				},
			},
		},
		courses: {
			inviteText:
				"To join the exercise, click this link: {{link}},\nAuthenticate with your PIN: {{code}}",
			joined: "Joined",
			externalInviteInfo:
				"Share the link below to invite colleagues outside your predefined team. They can join and collaborate on the scenario, but will not be counted towards response time KPIs.",
			pending: "Pending",
			invited: "Invited",
			left: "Left",
			sortingOptions: {
				name: "Sort by name",
				date: "Sort by date",
			},
			template: "Template",
			"scenario-generation-started":
				"Scenario generation started. You will receive an email when it is ready.",
			leadsToPhase: "Leads to phase",
			effects: {
				tvGlitch: "TV Glitch Effect",
				drone: "Drone Effect",
			},
			title: "Scenarios",
			subtitle:
				"Choose, analyze, or create new scenarios to practice various situations, make decisions, and improve response strategies.",
			subtitleChooseOnly:
				"Choose a scenario to practice various situations, make decisions, and improve response strategies.",
			search: "Search for scenario",
			name: "Name",
			tags: "Tag",
			description: "Description",
			"for-whom": "For whom",
			users: "Users",
			json: "JSON Input",
			industry: "Industry",
			"next-phase": "Next Phase",
			"companySize-label": "Company Size",
			status: "Status",
			[CourseStatus.Active]: "Active",
			[CourseStatus.Draft]: "Draft",
			companySize: {
				small: "Small (1-50 employees)",
				medium: "Medium-sized (51-500 employees)",
				large: "Large (501-4999 employees)",
				veryLarge: "Very large (5000+ employees)",
			},
			incidentType: {
				cyberAttack: "Cyber attack (e.g., ransomware, data breach)",
				naturalDisaster: "Natural disaster (e.g., earthquake, hurricane, flood)",
				pandemic: "Pandemic or health crisis",
				supplyChainDisruption: "Major disruption to the supply chain",
				utilityFailure: "Failure of essential utilities (e.g., electricity, water, internet)",
				physicalSecurityIncident: "Physical security breach (e.g., unauthorized access, sabotage)",
				terrorism: "Terrorism or civil unrest",
				equipmentFailure: "Critical equipment or infrastructure failure",
				personnelLoss: "Sudden loss of key personnel",
				financialCrisis: "Severe financial crisis or market crash",
			},
			specificDeficits: {
				inadequatePlans: "Insufficient emergency plans",
				overburdenedRoles: "Overloaded key roles (e.g., CEO, crisis management team leader)",
				outdatedInfrastructure: "Outdated IT infrastructure",
				lackOfCommunication: "Poor internal communication or silo mentality",
				supplierDependence: "High reliance on individual suppliers",
			},
			language: {
				language: "Language",
				select: "Select a language",
				label: "In which language should the scenario be generated?",
				english: "English",
				deutsch: "German",
				all: "All",
			},
			primaryIncidentType: "Primary Incident Type",
			specificCompanyDeficits: "Specific Company Deficits (optional)",
			specificNotes: "Further Specific Notes / restrictions (optional)",
			"specificNotes-placeholder":
				"e.g. affected systems, geographical location, regulatory requirements, specific vulnerabilities not listed above.",
			"invalid-email": "Invalid email",
			create: {
				button: "Create New Scenario",
				title: "Create New Scenario",
				"creation-description":
					"Choose how you want to create the scenario? There are two options available: \n- Manual, where you fill in everything yourself \n- Automatic, with the help of generative AI, you will only need to specify some parameters",
				"choose-creation-flow": "Choose creation flow",
				"manual-creation": "Manual creation",
				"generative-ai": "Generative AI",
			},
			generate: {
				title: "Generate Scenario",
				errors: {
					industry: "Industry is required",
					companySize: "Company size is required",
					primaryIncidentType: "Primary incident type is required",
				},
			},
			delete: {
				title: "Delete Scenario",
			},
			update: {
				title: "Update Scenario",
				text: "Text",
				json: "JSON",
			},
			upload: {
				title: "Click to upload or drag and drop",
				description: "(up to {{sizeInMB}} MB)",
				drop: "Drop file here",
				modalTitle: "Upload files",
				modalBtn: "Upload",
			},
			duplicate: {
				duplicateScenario: "Duplicate scenario",
				newTitle: "New title",
				copy: "Copy",
			},
			uploadedFiles: {
				selectFile: "Select a file to upload for this scenario",
			},
			start: {
				title: "Start Scenario",
				label: "Select scenario to start",
				"please-select": "Please select scenario",
				versionLabel: "Version to run",
				versionHint:
					"Only you can see this. Everyone else always gets the published version — including VR.",
				publishedVersion: "Published version",
				draftVersion: "Draft — not published",
				versionFallback: "Version {{id}}",
				"start-training-description":
					'Do you really want to start scenario with name "{{trainingName}}"?',
				"invite-colleagues-title": "Invite team",
				"invite-colleagues-description":
					"Invite your predefined team to collaborate on this scenario. Click the copy button to share the code and the link with your colleagues. Their response times will be tracked for KPI reporting.",
			},
			generalInvite: "General invite",

			progress: {
				content: "Content",
				whiteboard: "Whiteboard",
				emptyCanvas: "Empty canvas",
				pages: "pages",
				startDrawing: "Start drawing",
				stopDrawing: "Stop drawing",

				noFilesSelected: "No file selected",
				changeFile: "Change File",
				selectFile: "Select a file to share with your team",
				protocol: "Protocol",
				finalDecision: {
					elaboratedDecision: "Decision details: ",
					title: "Elaborate your decision",
					subtitle: "Please record how you would implement this decision in your organisation.",
					placeholder: "Please enter your text here...",
				},
				"content-cannot-be-displayed": "Content cannot be displayed",
				next: "Next Phase",
				save: "Save decision",
				information: "Information",
				"type-a-new-message": "Type a new message",
				"type-a-new-message-protocol": "Your protocol entry",
				complete: "Complete the scenario",
				restart: "Restart the scenario",
				send: "Send",
				"send-protocol": "Write to protocol",
				"time-up":
					"The time is running out, be careful, there are only {{leftTime}} minutes left to complete this stage",
				"time-up-empty":
					"The time for completing the stage is running out, pay attention to the timer",
				"no-content-available": "No content available",
				phase: "Phase",
				"no-messages-yet": "No messages yet!",
				you: "You",
				"end-session": "End session",
				"cant-sent-empty": "Can't send empty message",
				message: "Message",
				left: "left",
				chat: "Chat",
				"add-team": "Add Team",
				expired: "The invitation has expired",
				"join-course": "Join scenario",
				"first-login": {
					title: "Set your Name",
					"otp-title": "Enter PIN",
					"otp-description":
						"Enter the 4-digit PIN you received from the person leading the scenario.",
					"pin-format": "Please enter the 4-digit PIN.",
					description: "This is your name that will be displayed during the scenario.",
					continue: "Continue",
					["USER_NOT_FOUND"]: "Unable to join the scenario",
					["INVALID_OTP"]: "This PIN is invalid or the scenario has already ended.",
					next: "Next",
				},
				team: "Team",
				invited: "Invited",
				"invited-users": "Invited {{usersString}}",
				"user-joined": "Joined the scenario",
				"add-another": "Add another",
				"unknown-content": "Unknown content",
				"unsupported-content-type": "Unsupported content type",
				permissions: {
					[PARTICIPANT_PERMISSION_NONE]: "None",
					[PermissionRoles.PROTOCOL_WRITER]: "Writer",
					[PermissionRoles.PRESENTER]: "Presenter",
					[PermissionRoles.PARTICIPANT]: "Participant",
				},
				confirm: "Confirm decision",
				spoiler: "Spoiler",
				"i-see": "Close",
				no: "No",
				yes: "Yes",
				"sure-want-to-view-title": "Are you sure you want to view the help text?",
				"sure-want-to-view-description":
					"Click 'Yes' if you need assistance or 'No' to continue without it.",
				"requested-spoiler": "Requested help for phase {{phaseNumber}}",
				participant: "Participant",
				participants: "Participants",
				"responsible-person": "Responsible person",
				remove: "Remove",
				"removing-user-from-scenario": "Removing user from scenario",
				"remove-user-description":
					"Are you sure you want to remove user '{{data}}' from the scenario?",
				"removed-user": "Removed {{userData}} from the scenario",
				// The protocol as the paper form "Ereignisdokumentation" (BCM360-376).
				form: {
					title: "Incident log",
					date: "Date",
					exercise: "Exercise",
					keptBy: "Kept by",
					columns: {
						record: "Record of the event / minutes",
						time: "Time",
						assignee: "Responsible",
						done: "Done",
					},
					filters: {
						all: "All",
						open: "Open ({{count}})",
						done: "Done",
						allPhases: "All phases",
						phase: "Phase",
					},
					phase: "Phase {{id}}",
					phaseWithName: "Phase {{id}} · {{name}}",
					clock: "{{time}}",
					writtenBy: "written by {{name}}",
					editedBy: "changed by {{name}} at {{time}}",
					decision: "Decision",
					help: "Help",
					chosen: "Chosen: {{option}}",
					explanation: "Reason: {{text}}",
					assign: "– assign –",
					nobody: "– nobody –",
					assigneeHint: "Only if someone has something to do",
					doneAt: "{{time}}",
					open: "open",
					timeWarning_one: "{{count}} minute left in this phase",
					timeWarning_other: "{{count}} minutes left in this phase",
					timeWarningEmpty: "Time for this phase is running out",
					end: "Scenario ended",
					placeholder: "What happened, what was discussed or decided?",
					assignedToPhase: "Goes to {{phase}}",
					shortcut: "Ctrl + Enter adds it",
					add: "Add",
					edit: "Edit entry",
					save: "Save",
					cancel: "Cancel",
					empty: "No entries yet",
					emptyFilter: "No entries in this filter",
					invalidTime: "Time as HH:MM",
					textLabel: "Protocol entry",
					timeLabel: "Time",
					assigneeLabel: "Responsible",
					doneLabel: "Done",
				},
			},
			end: {
				[CourseProgressEnum.Success]:
					'You have successfully completed the scenario: "{{scenarioName}}"',
				[CourseProgressEnum.Failed]: 'Scenario "{{scenarioName}}" has not been completed',
				[CourseProgressEnum.IndeterminateOutcome]: 'The scenario "{{scenarioName}}" was aborted.',
				[CourseProgressEnum.NotStarted]:
					"The scenario with this ID was not found, make sure the data is correct",
				success: "Congratulations!",
				"time-up": "The time allotted for the stage was exhausted",
				"no-info": "No information on the result of the scenario is available",
				failure: "Failure",
				"auto-completed": "The scenario was automatically completed",
				"already-end": "The scenario has already been completed",
				congratulations: "Congratulations",
				"view-all-finished-scenarios": "View all finished scenarios",
				"go-to-scenarios-dashboard": "Go to scenarios dashboard",
				unfortunately: "Unfortunately",
				"scneario-progress-not-found": "Scenario progress not found",
			},
			"show-pin-title": "Meta Quest Connection PIN",
			"show-pin-description": "Enter this PIN in the input field on your Meta Quest.",
			connectAR: "connect AR",

			author: "Author",
			phaseName: "Phase name",
			id: "ID",
			PasteURL: "Paste URL for",
			AudioNotSupported: "Your browser does not support the audio element.",
			VideoNotSupported: "Your browser does not support the video element.",
			contentType: "Content Type",
			text: "Text",
			audio: "Audio",
			image: "Image",
			video: "Video",
			autoplay: "Autoplay",
			phaseEndText: "Phase end text",
			phaseEndTextExplain: "Text that is displayed at the end of the phase",
			decisions: "Decisions",
			decisionTitle: "Decision",
			timeLimit: "Time limit",
			inSeconds: "in seconds",
			TimeoutPhaseID: "Automatically selected option when time limit is reached",
			confirmation: "Confirmation required",
			none: "None",
			fromLeader: "From Leader",
			fromAll: "From All",
			decisionOptions: "Decision options",
			option: "Option",
			phaseID: "Phase ID",
			spoiler: "Spoiler",
			timeDelayedContent: "Time delayed content",
			add: "Add",
			remove: "Entfernen",
			content: "Content",
			startTime: "Start time",
			endTime: "End time",
			title2: "Title",
			newOption: "New option",
			UntitledScenario: "Untitled scenario",
			UnknownAuthor: "Unknown author",
			Success: "Success",
			Failed: "Failed",
			phaseEndResult: "Phase end result",
			newPhase: "New phase",
			newContent: "New content",
			newTitle: "New title",
			large: "360° Video",
			forWhom: "For whom",
			editDecision: "Edit decision",
			explanation: "Explanation",
			editSpoiler: "Edit spoiler",
			editTimeDelayedContent: "Edit Time Delayed Content",
			aiImage: {
				uploadFile: "Upload file",
				generateWithAi: "Generate with AI",
				prompt: "Prompt",
				promptPlaceholder: "Describe the image you want — subject, perspective, lighting, style.",
				promptHint: "Exact output dimensions per request — no cropping needed.",
				qualityTier: "Quality tier",
				tierBulk: "Bulk",
				tierBulkHint: "Phases & variants",
				tierHero: "Hero",
				tierHeroHint: "Titles & key scenes",
				perImage: "/ image",
				formats: "Formats",
				formatsHint: "(generate once → several formats in one job)",
				formatsManagedHint: "Presets are managed centrally under Settings → Formats & presets.",
				estimatedCost: "Estimated cost",
				estimateDetail: "({{variants}} variants × {{formats}} formats, {{tier}})",
				priceUnverified: "Price not yet verified — this estimate is a placeholder.",
				generate: "Generate images",
				generating: "Job running asynchronously …",
				asyncHint: "Asynchronous job — results appear automatically once they are ready.",
				results: "Results",
				jobLabel: "Job #{{id}}",
				variant: "Variant {{index}}",
				applyVariant: "Use variant {{index}}",
				regenerate: "Regenerate",
				cancel: "Cancel job",
				running: "Running",
				done: "Completed",
				failed: "Failed",
				cancelled: "Cancelled",
				noFormatSelected: "Please select at least one format.",
				promptRequired: "Please enter a prompt.",
				submitError: "The generation job could not be started.",
				selectError: "The variant could not be applied.",
				noModelHint: "No model is configured for phase images.",
			},
			mediaLibrary: {
				description:
					"Every file generated for this scenario — and, with the toggle, everything your company has. A file can be used in several scenarios; it is only stored once.",
				pageTitle: "Media library",
				descriptionGlobal:
					"Every file your company has — generated or uploaded, across every scenario. Assigning happens in the scenario itself: thumbnail, large image and trailer in the scenario form, phase content in the phase editor.",
				uploadedGroup: "Uploaded",
				uploadNew: "Upload a file",
				uploadFailed: "The upload failed.",
				pickTitle: "Choose from the media library",
				pickStyleTitle: "Choose a graphical style",
				pickEmptyStyles:
					"No graphical styles in the library yet. Upload a picture — once analysed it appears here as a graphical style.",
				pickEmpty:
					"Nothing in the library for this content type yet. Upload a file, or generate media in the intro-video wizard.",
				origin: { uploaded: "Uploaded", styleReference: "Graphical style" },
				choose: "Choose from the media library",
				scopeScenario: "This scenario",
				scopeCompany: "All scenarios",
				empty: "Nothing has been generated for this scenario yet.",
				noPreview: "No preview",
				kindVideo: "Video",
				kindImage: "Image",
				kindAudio: "Audio",
				kindFile: "File",
				kindTab: {
					all: "All",
					video: "Videos",
					image: "Images",
					audio: "Audio",
					other: "Other",
				},
				sortNewest: "Newest first",
				sortOldest: "Oldest first",
				sortNameAsc: "Name A–Z",
				sortNameDesc: "Name Z–A",
				closePreview: "Close preview",
				filterAllScenarios: "All scenarios",
				roleTrailer: "Intro video",
				roleContent: "Phase content",
				roleUnused: "Unused",
				roleElsewhere: "In {{times}} other scenarios",
				from: 'from "{{name}}"',
				useAsTrailer: "Use as intro video",
				removeFromScenario: "Remove from scenario",
				download: "Download",
				downloadFailed: "Download failed — please try again.",
				delete: "Delete",
				actionFailed: "That action could not be completed.",
				replaceTitle: "Replace the intro video?",
				replaceBody:
					"This scenario already has an intro video. The current one will be replaced — trainees will see the new one from now on. The file itself is kept and can be chosen again at any time.",
				replaceConfirm: "Replace",
				folders: {
					title: "Folders",
					allFiles: "All files",
					newFolder: "New folder",
					open: 'Open folder "{{name}}"',
					files_one: "{{count}} file",
					files_other: "{{count}} files",
					subfolders_one: "{{count}} subfolder",
					subfolders_other: "{{count}} subfolders",
					namePlaceholder: "Folder name",
					rename: "Rename",
					delete: "Delete folder",
					expand: "Expand",
					collapse: "Collapse",
					deleteTitle: 'Delete the folder "{{name}}"?',
					deleteBody:
						"Only an empty folder can be deleted. Files are never deleted along with a folder — move them out first, or drag them onto All files.",
					deleteConfirm: "Delete",
					includeSubfolders: "Include subfolders",
					dropHint:
						"Drag a file onto a folder to move it there. Drag it onto All files to take it out again. Drag a folder onto another folder to nest it.",
					emptyFolder: "Nothing in this folder yet. Drag a file here from the library.",
				},
			},
			jobsCosts: {
				title: "Jobs & costs",
				columns: {
					job: "Job",
					type: "Type",
					details: "Details",
					formats: "Formats",
					status: "Status",
					cost: "Cost",
				},
				empty: "No AI jobs have run for this scenario yet.",
				scenarioTotal: "Cost of this scenario — including phase TTS and AI images",
				estimatePrefix: "~",
				phaseDetail: "Phase {{phase}} · {{variants}} variants · {{model}}",
				scenarioDetail: "{{variants}} variants · {{model}}",
				infra: {
					async: "Async job · results are reconciled automatically",
					registry: "Model swappable per use case, without a code change",
					origin: "Origin: Hetzner Object Storage (DE)",
					cdn: "CDN: Bunny.net (EU)",
				},
				type: {
					PHASE_IMAGE: "Images",
					STORYBOARD: "Storyboard",
					SCENE_IMAGE: "Scene images",
					NARRATION_PREVIEW: "Voice preview",
					NARRATION_TTS: "Narration",
					SCENE_VIDEO: "Scene videos",
					VIDEO_EXPORT: "Video export",
				},
				status: {
					QUEUED: "Queued",
					RUNNING: "Running",
					SUCCEEDED: "Completed",
					FAILED: "Failed",
					CANCELLED: "Cancelled",
				},
			},
			introVideo: {
				title: "Video assistant",
				description:
					"A guided six-step flow: storyboard → style & example images → Narration → Audio → scene videos → cut & export. The finished video can become the scenario's intro, or go to the media library to be used as phase content.",
				inProgress: "Running — step {{step}}",
				started: "Started — step {{step}}",
				archivedHeading: "Archived assistants",
				archivedRow: "Archived on {{date}} · up to step {{step}}",
				finished: "✓ Finished",
				continue: "Continue the assistant",
				openFinished: "Open the finished video",
				spent: "Spent so far",
				notStarted: "Not started yet",
				steps: {
					storyboard: "Storyboard",
					storyboardHint:
						"Prompt + target length → a clear instruction per scene: what is shown and for how long",
					style: "Style & example images",
					styleHint:
						"Choose a graphical style → example image and description per scene, iterable one by one",
					narration: "Narration",
					narrationHint: "The script, written like a trailer — read, check, iterate with feedback",
					audio: "Audio",
					audioHint: "Choose the voice and subtitle style, then voice the script",
					sceneVideos: "Scene videos",
					sceneVideosHint: "Generate, review, iterate and approve each scene separately",
					cutExport: "Cut & export",
					cutExportHint: "Trim, reorder, and stitch into one video",
					storyboardEstimate: "~$0.02",
					styleEstimate: "~$0.28",
					narrationEstimate: "~$0.02",
					audioEstimate: "~$0.10",
					sceneVideosEstimate: "~$3.60",
					cutExportEstimate: "~$0.10",
				},
				forecast: "Total forecast (budget band $3–6 per 30-second video)",
				forecastValue: "~$4.10",
				start: "Start the assistant",
			},
			tts: {
				uploadFile: "Upload File",
				generateFromText: "Generate from Text",
				textToConvert: "Text to convert to speech",
				textPlaceholder: "Enter the text that should be spoken in this phase...",
				voiceLanguage: "Voice Language",
				allLanguages: "All Languages",
				voice: "Voice",
				selectVoice: "Select Voice",
				changeVoice: "Change Voice",
				noVoiceSelected: "No voice selected",
				voiceFilters: "Filter Voices",
				gender: "Gender",
				age: "Age",
				useCase: "Use Case",
				category: "Category",
				previewVoice: "Preview",
				confirmVoice: "Select Voice",
				allGenders: "All Genders",
				allAges: "All Ages",
				allUseCases: "All Use Cases",
				allCategories: "All Categories",
				searchPlaceholder: "Search voices …",
				noVoices: "No voices match these filters.",
				values: {
					male: "Male",
					female: "Female",
					neutral: "Neutral",
					young: "Young",
					middle_aged: "Middle-aged",
					old: "Older",
					conversational: "Conversational",
					informative_educational: "Informative & educational",
					narrative_story: "Narration & story",
					entertainment_tv: "Entertainment & TV",
					social_media: "Social media",
					characters_animation: "Characters & animation",
					advertisement: "Advertising",
					professional: "Professional",
					premade: "Premade",
					cloned: "Cloned",
					generated: "Generated",
					american: "American",
					british: "British",
					australian: "Australian",
					irish: "Irish",
					swedish: "Swedish",
					transatlantic: "Transatlantic",
					standard: "Standard",
				},
				generateAudio: "Generate Audio",
				generating: "Generating...",
				generateError: "Audio generation failed. Please try again.",
			},
			mustBeSet: {
				phaseEndText: "Phase End Text must be set",

				decisionOption: "Decision option must be set",
				decisionName: "Decision name must be set",
				timeLeftDecision: "Time left decision must be set",
				timeLimit: "Time limit must be set",
				confirmationRequired: "Confirmation required must be set",
			},
		},
		tutorials: {
			title: "Title",
		},
		phaseNode: {
			audioContent: "Audio file",
			phaseName: "Untitled Phase",
			spoilerAvailable: "Spoiler available",
			delayedContentAvailable: "Content available with delay",
			decisionsOptions: "Decision options",
			decisionsOption: "Decision option",
			nextPhase: "Leads to the next phase",
			newPhase: "New phase",
			invalidNode: "This phase has validation errors",
			validationError: {
				contentRequired: "Content is required",
				decisionOptionRequired: "Decision option text must be set",
				decisionNameRequired: "Decision name must be set",
				timeLeftDecisionRequired: "Time left decision must be set",
				timeLimitRequired: "Time limit must be set",
				confirmationRequired: "Confirmation type is required for multiple options",
				phaseEndTextRequired: "Phase end text must be set",
				endTimeInvalid: "End time must be at least 10s after start time",
				generic: "Invalid field: {{path}}",
			},
		},
		editor: {
			rightPanel: {
				graph: "Flow",
				introVideo: "Video (AI)",
				media: "Media",
				jobsCosts: "Jobs & costs",
				versions: "Versions",
			},
			publish: {
				statusDraft: "Draft",
				statusActive: "Live",
				unpublishedChanges: "Changes not published",
				nothingLive: "nothing yet",
				unnamedVersion: "Version {{id}}",
				draftName: "your unpublished draft",
				editingDraft:
					"Published version: {{published}}. You are editing: {{editing}} — publish it to make these changes count.",
				editingLive: "Published version: {{published}}. That is what you are editing.",
				publish: "Publish",
				publishTitle: "Publish this scenario?",
				publishWarning:
					"The saved draft becomes what every participant plays — in the web app and in VR — for every session started from now on. Sessions already running are not affected.",
				publishFirstWarning:
					"This scenario has never been published. Publishing makes it visible to participants for the first time, in the web app and in VR alike. Sessions already running are not affected.",
				republishWarning:
					"The version that was live before goes back to participants — in the web app and in VR. Nothing has changed since you withdrew it.",
				publishConfirm: "Publish",
				publishFailed: "The scenario could not be published. Try again.",
				incomplete: "The scenario is not finished yet — {{issues}}",
				issuePhase: "Phase {{number}}{{name}}",
				issueScenario: "Scenario",
				issueLine: "{{where}}: {{fields}} missing",
				problem: {
					UNKNOWN_TARGET: "an option leads to phase {{target}}, which does not exist",
					FALLBACK_NOT_AN_OPTION:
						"the option selected automatically when the time limit is reached (phase {{target}}) is not one of this phase's options",
					NO_START: "no start phase — every phase is the target of an option",
					SEVERAL_STARTS:
						"several start phases ({{phases}}) — exactly one phase may have no option leading to it",
					START_WITHOUT_DECISION: "the start phase needs a decision",
				},
				field: {
					content: "content",
					phaseName: "phase name",
					scenarioName: "scenario name",
					contentType: "content type",
					decisionName: "decision name",
					decisionOptions: "decision options",
					title: "title",
					structure: "structure",
				},
				unpublish: "Unpublish",
				unpublishTitle: "Unpublish this scenario?",
				unpublishWarning:
					"The scenario is hidden from participants — in VR too. Sessions already running are not affected. You can publish it again at any time.",
				unpublishConfirm: "Unpublish",
				unpublishFailed: "The scenario could not be unpublished. Try again.",
				unreachable: "The server cannot be reached — the scenario was not changed.",
			},
			versions: {
				saveHeading: "Save the live state as a version",
				saveHint:
					"Keeps what is published right now as a named version you can return to. Your unpublished edits live in the draft above and are not included.",
				labelPlaceholder: "Name, e.g. \u201cbefore the March revision\u201d",
				save: "Save version",
				loading: "Loading versions …",
				empty: "Nothing published yet — publishing the draft records the first version.",
				emptyButLive:
					"This scenario is live, but was published before versions were kept — so there is no history yet. The next publish records one.",
				draftHeading: "Working copy",
				draftBadge: "Draft",
				draftHint:
					"Everything you save lands here. Nobody plays it until you publish — use Publish above.",
				lastPublished: "Last published",
				saved: "Saved as",
				untitled: "Version {{id}}",
				active: "Active",
				publishedAt: "Published on {{date}}",
				createdAt: "Created {{date}}",
				createdBy: "by {{name}}",
				rename: "Rename",
				renameTitle: "Rename version",
				renameSave: "Save name",
				publish: "Publish",
				publishTitle: "Publish this version?",
				publishWarning:
					"\u201c{{label}}\u201d becomes what every participant plays, in the web app and in VR alike, including sessions started from now on. The current state is kept as a version of its own.",
				publishConfirm: "Publish",
				delete: "Delete",
				view: "Preview",
				deleteTitle: "Delete this version?",
				deleteWarning:
					"\u201c{{label}}\u201d is removed from the history for good. What participants play is unaffected.",
				deleteConfirm: "Delete",
				unreachable: "The server cannot be reached — nothing was changed.",
				deleteFailed: "The version could not be deleted. Try again.",
				saveFailed: "The version could not be saved. Try again.",
				renameFailed: "The name could not be saved. Try again.",
				publishFailed: "The version could not be published. Try again.",
			},
			newPhase: "New phase",
			newContent: "New content",
			searchPhases: "Search phases...",
			noResults: "No matching phases",
			deletePhaseTitle: "Delete Phase",
			deletePhaseConfirmationUnnamed:
				"Are you sure you want to delete this phase? This will remove all its connections.",
			deletePhaseConfirmation:
				'Are you sure you want to delete phase "{{phaseName}}"? This will remove all its connections.',
			staleDraft: {
				title: "The draft was changed meanwhile",
				text: "Since you opened the editor, the draft was saved – in another window or by an AI client. Reload shows the newer state (your changes are lost); Save anyway overwrites it with yours.",
				reload: "Reload",
				overwrite: "Save anyway",
				keepEditing: "Cancel",
			},
			unsavedChanges: "Unsaved changes",
			unsavedChangesWarning: "You have unsaved changes. Are you sure you want to leave?",
			discardChanges: "Discard changes",
			emptyStateHint: "Click a phase to edit it, or drag a new one from the sidebar",
			sectionContent: "Content",
			sectionEndConditions: "End Conditions",
			sectionDecisions: "Decisions",
			sectionSpoiler: "Spoiler",
			sectionTimeDelayed: "Time-Delayed Content",
			sectionEffects: "Effects",
			decisionsCount: "{{count}} decision(s)",
			delayedItemsCount: "{{count}} item(s)",
			toggleDecisionLabels: "Toggle decision labels",
			undo: "Undo",
			redo: "Redo",
			autoLayout: "Auto layout",
			invalidNode: "This phase has validation errors",
		},
		course: {
			title: "Scenario",
			subtitle: "Manage your Scenario.",
			tabs: {
				general: "General",
				user: "Users",
				roles: "User roles",
			},
			courseInformation: "Scenario Information",
			"all-scenarios": "All scenarios",
			"get-started": "Get started",
			"for-whom": "For whom",
			"related-scenarios": "Related scenarios",
			"no-related-scenarios": "No related scenarios",
			"see-details": "See details",
			overview: "Overview",
			"no-tag": "No tag",
		},
		analytics: {
			"page-title": "Reports",
			export: "Export",
			"only-the-protocol": "Only the protocol",
			"protocol-with-related-content": "The protocol with revealed content",
			"delete-scenario-progress-title": "Delete scenario progress",
			"file-export-error": "An error occurred while exporting a file",
			"sure-want-to-delete-report":
				"Do you really want to delete the report of scenario '{{courseName}}' completion for the period '{{period}}'?",
		},
		dashboard: {
			title: "Welcome",
		},
		mediaCosts: {
			title: "AI costs",
			description:
				"What your company has spent on AI-generated media — phase images, phase speech, generated videos and library tracks. The figures come from the cost ledger and never change retrospectively.",
			total: "Total for the period",
			empty: "No costs recorded for this period.",
			byCourse: "By scenario",
			byUseCase: "By use case",
			byMonth: "By month",
			deletedScenario: "Deleted scenario (#{{id}})",
			courselessHint:
				"{{amount}} is spend with no scenario attached — generated library tracks, for instance. This breakdown therefore adds up to less than the total.",
			columns: {
				scenario: "Scenario",
				useCase: "Use case",
				month: "Month",
				amount: "Amount",
			},
			window: {
				from: "From",
				to: "To",
				reset: "Reset period",
				applied: "Showing {{from}} to {{to}}",
				defaultHint: "With no dates given, the last twelve calendar months are shown.",
			},
		},
		mediaPresets: {
			title: "Formats & presets",
			description:
				"Central management of output formats and the AI model registry. Adding a model or changing a price is a setting — not a code change.",
			unverifiedPricing: {
				title: "{{count}} model(s) with unverified pricing",
				description:
					"The stored prices come from the design draft and are placeholders. Cost estimates stay marked as unverified until each price has been checked against the provider's live pricing and confirmed.",
			},
			music: {
				title: "Music library",
				description:
					"Background music for generated videos. The library is global: every company picks from this one list, and only a platform admin can change it.",
				empty: "No tracks in the library yet.",
				uploadTitle: "Upload a track",
				generateTitle: "Generate a track",
				editTitle: "Edit track",
				licenceMissing: "No licence on file",
				licenceWarning:
					"{{times}} track(s) with no licence on file. Please complete them — a library without licence evidence cannot be used.",
				generateCostHint:
					"Generating costs money and is billed to the company that triggers it, with no scenario attached. The track appears here once the audio has been rendered.",
				columns: {
					track: "Track",
					origin: "Origin",
					duration: "Length",
					licence: "Licence",
				},
				origin: {
					uploaded: "Uploaded",
					generated: "AI-generated",
				},
				actions: {
					upload: "Upload a track",
					generate: "Generate a track",
					generateSubmit: "Generate",
					play: "Play",
					stop: "Stop",
				},
				fields: {
					name: "Title",
					namePlaceholder: "Rising tension",
					description: "Description",
					descriptionPlaceholder: "Subtle, building",
					file: "Audio file",
					fileHint: "MP3 or WAV. The file is stored in our own object storage.",
					prompt: "Description for the model",
					promptPlaceholder:
						"Calm, atmospheric background music without vocals, subtly building, matter-of-fact in character.",
					durationSeconds: "Length (seconds)",
					licence: "Licence",
					licencePlaceholder: "e.g. CC0, or the licensor's name",
					licenceGeneratedPlaceholder: "Leave empty for the provider's licence",
					licenceHint: "Required. Record the licence under which this track may be used.",
					licenceGeneratedHint:
						"Optional. Left empty, the track is filed under the provider's own licence terms.",
				},
			},
			formats: {
				title: "Output formats",
				description:
					"Global presets apply to every company. Companies can add their own presets and hide global ones.",
				empty: "No formats configured yet.",
				createTitle: "Create format",
				editTitle: "Edit format",
			},
			models: {
				title: "AI model registry",
				description:
					"One model per use case is the default. Price, EU evidence and availability are maintained here.",
				empty: "No models registered yet.",
				editTitle: "Edit model",
			},
			columns: {
				label: "Label",
				dimensions: "Dimensions",
				aspectRatio: "Aspect ratio",
				mediaKind: "Media type",
				scope: "Scope",
				status: "Status",
				model: "Model",
				provider: "Provider",
				price: "Price",
			},
			fields: {
				key: "Key",
				label: "Label",
				width: "Width (px)",
				height: "Height (px)",
				aspectRatio: "Aspect ratio",
				mediaKind: "Media type",
				displayName: "Display name",
				unitPriceUsd: "Price per unit (USD)",
				unitPriceHint: "Used for every cost estimate and settlement in the product.",
				pricesVerified: "Price verified",
				pricesVerifiedHint:
					"Only enable once the price has been checked against the provider's current pricing.",
				enabled: "Active",
			},
			mediaKind: {
				image: "Image",
				video: "Video",
				both: "Image & video",
			},
			scope: {
				global: "Global",
				company: "Company",
			},
			status: {
				enabled: "Active",
				disabled: "Inactive",
				default: "Default",
				priceUnverified: "Price unverified",
			},
			useCase: {
				phase_image: "Phase image",
				storyboard: "Storyboard",
				scene_image: "Scene image",
				narration_tts: "Narrator voice",
				scene_video: "Scene video",
				video_export: "Video export",
				music: "Background music",
			},
			provider: {
				openai: "OpenAI",
				elevenlabs: "ElevenLabs",
				black_forest: "Black Forest Labs",
				openrouter: "OpenRouter",
				fal: "fal.ai",
				self_hosted: "Self-hosted",
				internal: "Internal",
			},
			unit: {
				per_image: "per image",
				per_second: "per second",
				per_scene: "per scene",
				per_1k_tokens: "per 1,000 tokens",
				per_track: "per track",
				per_export: "per export",
			},
			actions: {
				addPreset: "Add format",
				edit: "Edit",
				enable: "Enable",
				disable: "Disable",
				delete: "Delete",
				makeDefault: "Make default",
			},
		},
		aiGenerated: {
			badge: "AI-generated",
			description:
				"This media was generated entirely by an artificial intelligence and shows no real people or events.",
		},
		sidebar: {
			mediaPresets: "Formats & presets",
			mediaLibrary: "Media",
			mediaCosts: "AI costs",
			kpiDashboard: "KPI Dashboard",
			users: "Users",
			roles: "User roles",
			logout: "Logout",
			accounts: "Accounts",
			settings: "Settings",
			accountSettings: "Account Settings",
			profile: "My Profile",
			companies: "Company Management",
			back: "Back to your account",
			all: "All",
			courses: "Scenarios",

			cybersecurityIncidentBasics: "Cybersecurity Incident Basics",
			company: "Company",
			menu: "Menu",
			tutorials: "Tutorials",
			analytics: "Reports",
			"my-profile": "My Profile",
			"current-scenario": "Current Scenario",
		},
		kpiDashboard: {
			all: "All",
			completionTime: "Completion Time",
			responseTime: "Response Time",
			courses: "Scenarios",
			avgResponseTimeInfo: "Including success, failed, aborted outcomes",
			avgPathDeviationInfo: "Including only success outcomes",
			avgCompletionTimeInfo:
				"From the leader starting the scenario until the run ends, including the wait until everyone has joined. Only success and failed outcomes",
			avgComparisonInfo:
				"Response time includes success, failed, and aborted outcomes while completion time includes only success and failed outcomes",
			"7d": "7d",
			"30d": "30d",
			"90d": "90d",
			vsPreviousPeriod: "compared to previous period",
			failedScenarios: "Failed Scenarios",
			successRate: "Success Rate",
			outcomeDistributionTitle: "Outcome Distribution per Scenario",
			timeComparisonTitle: "Response & Completion Time by Scenario",
			resultDistributionTitle: "Result Distribution",
			pathEfficiencyTitle: "Path Deviation — Actual vs Optimal Stages",
			"indeterminate outcome": "Aborted",
			runs: "runs",
			date: "Date",
			actualStages: "Actual Stages",
			optimalStages: "Optimal Stages",
			pageTitle: "KPI Dashboard",
			title: "KPI Dashboard",
			subtitle: "Scenario performance metrics for your organization",
			avgCompletionTime: "Avg Completion Time",
			avgResponseTime: "Avg Response Time",
			avgPathDeviation: "Avg Path Deviation",
			pathDeviation: "Path Deviation",
			totalScenarios: "Total Scenarios",
			stages: "stages",
			trendTitle: "Performance Over Time",
			perScenarioTitle: "Per Scenario Breakdown",
			scenario: "Scenario",
			total: "Total",
			success: "Success",
			outcome: "Outcome",
			failed: "Failed",
			noData: "No data available for this period",
			deleteData: "Delete KPI Data",
			deleteKPITitle: "Clear KPI Data",
			deleteKPIWarning: "Please confirm deletion of your saved KPI data.",
			deleteKPIConfirm: "Confirm",
			custom: "Custom",
			deleteRecentlyUsedWarning: "",
			day: "Day",
			week: "Week",
		},
		errors: {
			scenarioInfoNotFound: "Scenario information not found",
			fileSizeExceeded: "File size exceeded 100 MB",
			requiredFields: "Not all necessary fields are filled.",
			expectedNumber: "Number expected / enter 0 if not needed.",
			customColumnAlreadyExists: "Custom column already exists.",
			invalidEmail: "Invalid email",
			invalidPassword: "Invalid password",
			passwordGuidelinesNotMet: "Password guidelines not met",
			termsAcceptanceMissing: "Terms acceptance missing",
			invalidValue: "Invalid value",
			invalidData: "Invalid data",
			userNotConfirmed: "User not confirmed",
			passwordMismatch: "Passwords do not match",
			invalidToken: "Invalid token",
			emailAlreadyTaken: "Email already taken",
			wentWrong: "Something went wrong",
			unauthorized: "Unauthorized",
			invalidAuthCode: "Invalid auth code",
			invalidOtp: "Invalid OTP",
			nameMissing: "Name missing",
			wrongUrlShouldBeLinkedinProfile: "Wrong URL. Should be a LinkedIn profile URL",
			notEnoughCredits: "Not enough credits",
		},
		mcpAuthorize: {
			title: "Connect an AI client",
			description: "{{client}} wants to work in the scenario editor as {{user}}.",
			canRead: "Read scenarios, versions and the media library",
			canEdit: "Create, edit, publish and delete scenarios, and upload files",
			canPaid: "Generate AI media – paid, each time only after your confirmation in the client",
			returnsTo: "Afterwards you return to {{host}}.",
			revoke: "You can disconnect it at any time under Account › Connected AI clients.",
			allow: "Allow",
			deny: "Deny",
			notAllowed: "Only Global Admins can connect AI clients to the scenario editor.",
			back: "Back to the client",
			invalid:
				"This request is invalid or has expired. Start the connection again in the AI client.",
		},
		account: {
			mcpConnections: {
				title: "Connected AI clients",
				description: "These AI clients may work in the scenario editor as you.",
				empty: "No AI client connected.",
				connected: "Connected on {{date}}",
				lastUsed: "last used {{date}}",
				disconnect: "Disconnect",
				guide: {
					title: "How to connect an AI assistant",
					intro:
						"With an AI assistant such as Claude or ChatGPT you can create and edit scenarios in a conversation. The assistant works as you and can do everything the scenario editor can. Only Global Admins can connect one.",
					addressTitle: "Address of the BCM360 server",
					copy: "Copy",
					copied: "Copied",
					copyAddress: "Copy address",
					copyCommand: "Copy command",
					claudeTitle: "Claude (claude.ai or Claude Desktop)",
					claudeSteps: [
						"In Claude, open the settings and add your own connector under “Connectors” (“Add custom connector”). This needs a Pro, Max, Team or Enterprise plan.",
						"Enter “BCM360” as the name and paste the address from above.",
						"Click “Connect”: BCM360 opens. Log in as usual, with your two-factor code, and choose “Allow”.",
						"Back in Claude, the BCM360 tools are ready. To try it: “List my scenarios.”",
					],
					claudeCodeTitle: "Claude Code",
					claudeCodeAfter:
						"Then type “/mcp” in Claude Code, choose “bcm360” and log in – the same page with “Allow” opens.",
					chatgptTitle: "ChatGPT",
					chatgptText:
						"In the settings under “Connectors”, switch on developer mode, create a new connector with the address from above and choose OAuth as authentication. BCM360 then opens for login and consent.",
					goodToKnowTitle: "Good to know",
					goodToKnow: [
						"The assistant only changes the draft. Participants see changes only after publishing.",
						"Paid steps such as AI images or voice recordings are named with their price first and run only after your confirmation.",
						"If you work in the editor at the same time, it reports a draft changed meanwhile before anything is overwritten.",
						"A connection stays for 30 days without use; after that, simply connect again. “Disconnect” ends it at once.",
					],
				},
			},
			emailOtpDescription: "Increase your account security with a one-time code sent to your email",
			connectionScraping: "LinkedIn-Connections",
			enableConnectionScraping: "active the scraping of your linkedin connections",
			enableConnectionScrapingDescription:
				"This allows you to identify which prospects you or your colleagues are already connected to on Linkedin.",
			linkedinAutomation: "LinkedIn Automation",
			enableAutoAccept: "Enable Auto Accept",
			enableAutoAcceptDescription: "Automatically accept connection requests",
			profileVisits: "Profile Visits",
			warningPremiumAccountRequired: "This feature is only available for premium accounts",
			enableProfileVisitsScraping: "Enable Profile Visits Scraping",
			changePassword: "Change password",
			currentPassword: "Current password",
			disableTwoFactorAuth: "Disable 2FA",
			enableTwoFactorAuth: "Enable 2FA",
			enableTwoFactorAuthDescription:
				"Scan the QR code with your authenticator app and enter the code below",
			disableTwoFactorAuthDescription: "Enter the code from your authenticator app",
			profileSecurityDescription:
				"Increase your account security with 2FA through an authentication app",
			registerWithTwoFactorAuthDescription:
				"It is recommend to set up two factor authentication, scan the QR code with your authenticator app and enter the code below",
			newPassword: "New Password",
			profileInformation: "Profile information",
			profileSecurity: "Profile security",
			repeatPassword: "Repeat new password",
			tabs: {
				general: "General",
				notifications: "Notifications",
				linkedinConnections: "LinkedIn Connections",
			},
			title: "Personal Info",
			description: "Update your personal details and settings here.",
			save: "Save changes",
			saving: "Saving...",
			cancel: "Cancel",
			linkedinConnections: "LinkedIn Connections",
		},
		basics: {
			none: "Participant",
			loading: "Loading",
			create: "Create",

			saveAndClose: "Save and Close",
			selectColumns: "Select columns",
			wait: "Please wait",
			generate: "Generate",
			reset: "Reset",
			pleaseSelect: "Please select",
			noResults: "No results",
			account: "Account",
			actions: "Actions",
			admin: "Admin",
			back: "Back",
			block: "block",
			cancel: "Cancel",
			change: "Edit",
			code: "Code",
			otp: "OTP Code",
			confirmationClose: "Are you sure you want to close the window?",
			confirm: "Confirm",
			confirmPassword: "Confirm Password",
			createSomething: "Create {{something}}",
			delete: "Delete",
			description: "Description",
			dragAndDropImage: "Drag & Drop",
			edit: "Edit",
			editSomething: "Edit {{something}}",
			email: "E-mail Address",
			enterSomething: "Enter {{something}}",
			firstName: "First Name",
			home: "Home",
			label: "Label",
			lastName: "Last Name",
			linkedinUrl: "LinkedIn URL",
			logout: "Logout",
			name: "Name",
			noData: "No Data",
			password: "Password",
			passwordGuidelines: {
				minimum: "Password must contain at least 8 characters",
				number: "Password must contain at least 1 number",
				specialChar: "Password must contain at least 1 special character",
				uppercase: "Password must contain at least 1 uppercase",
				lowercase: "Password must contain at least 1 lowercase",
			},
			permissions: "Permissions",
			resetPassword: "Reset password",
			role: "Role",
			save: "Save",
			saveChanges: "Save changes",
			search: "Search {{something}}...",
			select: "Select",
			selectSomething: "Select {{something}}",
			setPassword: "Set password",
			status: "Status",
			termsAndConditions: "<noLink>I have read the</noLink> <link1>Terms and Conditions</link1>",
			privacyPolicy: "<noLink>I have read and accepted the</noLink> <link1>Privacy Policy</link1>",
			upload: "Upload",
			value: "Value",
			warning: "Attention",
			filter: "Filter",
			clear: "Reset All Filters",
			close: "Close",
			rows: "Rows",
			rowsPerPage: "Rows per page",
			rowsOf: "of",

			columns: "Columns",
			columnsShown: "Columns shown",
			export: "Export",
			next: "Next",
			previous: "Previous",
			addCriteria: "Add Criteria",
			copyCriteria: "Copy Criteria",
			archive: "Archive",
			unarchive: "Unarchive",
			success: "Operation processing",
			goHome: "Go to Scenarios",
			"enter-your": "Enter your ",
			"enter-your-firstname": "Enter your first name",
			"enter-your-lastname": "Enter your last name",
			"enter-your-password": "Enter your password",
			"enter-your-confirm-password": "Confirm your password",
			"not-support-tag": "Your browser does not support this tag.",
			thumbnail: "Thumbnail",
			"thumbnail-description": "This is the thumbnail on the scenario overview page.",
			"trailer-video": "Trailer Video",
			"trailer-video-description": "This is the video on the scenario details page.",
			"large-image": "Large Image",
			"large-image-description": "This is the large image on the scenario details page.",
			"content-scenario": "Content of the scenario",
			"confirm-invite-description": "To confirm the invitation, please enter your details",
			"invitation-expired": "The invitation is expired",
			"invitation-token-invalid": "Invitation token is invalid",
			"invitation-already-accepted": "Invitation has already been accepted",
			"accepting-invitation": "Accepting invitation",
			"update-pass-description": "Please choose a password that hasn’t been used before. ",
			"request-error": "Request Error",
		},
		errorPages: {
			buttonText: "Take Me Home",
			description:
				"You either tried some shady route or you came here by mistake. Whichever it is, try using the navigation.",
			notFound: "404: The page you are looking for isn't here",
			serverError: "500: Internal Server Error",
			unauthorize: "401: Authorization required",
			forbidden: "403: Forbidden",
		},
		forgotPassword: {
			forgotPasswordCta: "Send reset link",
			submittedFeedbackMessage:
				"Check your inbox. We send an e-mail with instructions on how to reset your password!",
			title: "Forgot password",
			description: "Please enter your registered email address. We will send you a reset link.",
			send: "Send",
			"back-to-login": "Back to login",
		},
		inviteUser: {
			button: "Invite new user",
			modal: {
				cta: "Invite",
				emailPlaceholder: "Enter email",
				firstNamePlaceholder: "Enter first name",
				lastNamePlaceholder: "Enter last name",
				rolePlaceholder: "Select role",
				title: "Invite user",
			},
		},
		login: {
			emailPlaceholder: "Your e-mail address",
			forgotPasswordCta: "Forgot Password?",
			loginCta: "Login",
			passwordPlaceholder: "Your password",
			title: "Welcome back",
			description: "Welcome back! Please enter your details.",
			invalidData: "email or password wrong",
			"check-email": {
				title: "Check your email",
				description: "We sent a verification link to ",
				"enter-code": "Enter code manually",
				"back-to-login": "Back to log in",
			},
			"remember-me": "Remember me",
		},
		modals: {
			addAvatar: {
				description: "<underline>Click to upload</underline> or drag and drop",
				fileSize: "Max file size is 3 MB",
				title: "Upload File",
			},
			deleteRole: {
				title: "Delete role",
			},
			deleteUser: {
				title: "Delete user",
			},
			editUser: {
				rolePlaceholder: "Select Roles",
				title: "Edit user",
			},
		},
		emailOtp: {
			back: "Back",

			title: "Email Verification",
			description:
				"We have sent a 6-digit code to your email address. Please enter the code to sign in.",
			code: "Verification code",
			codePlaceholder: "123456",
			cta: "Confirm",
			resend: "Resend code",
			resending: "Sending...",
		},
		twoFA: {
			useEmailOtp: "Get code via email",
			code: "Code",
			cta: "Confirm",
			title: "Enter 2FA Code",
			description: "Please enter the  code from your authenticator application.",
		},
		users: {
			title: "Users",
			search: "users",
			resendInvite: "Resend invite",
			status: {
				expired: "Expired",
				active: "Active",
				invited: "Invited",
				blocked: "Blocked",
			},
		},
		userRoles: {
			title: "Roles",
			button: "User roles",
			modal: {
				create: {
					permissionPlaceholder: "Select Permissions",
					title: "Create role",
				},
				edit: {
					title: "Update role",
				},
			},
			placeholders: {
				codePlaceholder: "Enter",
				namePlaceholder: "Enter name",
			},
		},
	},
};
